// Sales order detail (BUILD_SPEC.md §5.7, §8.4 step 1). Editable header+lines
// while DRAFT (mirrors DispatchDetail.tsx's DraftDispatchEditor/PostedView
// split, adapted to sales orders' 4-state lifecycle: DRAFT -> OPEN ->
// COMPLETE|CANCELLED, with cancel reachable from either DRAFT or OPEN — see
// services/api/src/modules/sales/sales-orders.ts's transitionSalesOrder calls).
// Complete and Cancel are both terminal (no transition leads back out of
// either — sales-orders.test.ts pins CANCELLED-after-COMPLETE as a 409), so
// both are wrapped in ConfirmButton with consequence text verified against
// that module's actual behaviour: opening/completing/cancelling never touches
// stock, dispatches, or invoices — it only ever updates this document's own
// status (transitionSalesOrder is a single-table UPDATE).
import { useMemo, useState } from 'react';
import { Alert, Button, Group, Loader, Paper, Stack, Table, Text, Textarea, Title } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { SalesOrderResponse } from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useCanSell } from '../../auth/permissions';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { CustomerName, SiteName } from './components/EntityNames';
import { CustomerSelect } from './components/CustomerSelect';
import { SiteSelect } from './components/SiteSelect';
import {
  emptySalesOrderLineRow,
  salesOrderLinesValid,
  toSalesOrderLineInputs,
  SalesOrderLinesEditor,
  type SalesOrderLineFormRow,
} from './components/SalesOrderLinesEditor';
import { centsToRand, formatDateTime, formatLineQty, formatMoneyCents } from './format';
import {
  LOOKUP_LIMIT,
  useAllProducts,
  useCancelSalesOrder,
  useCompleteSalesOrder,
  useDispatchesForCustomer,
  useOpenSalesOrder,
  useSalesOrder,
  useUpdateSalesOrder,
  type SalesOrderUpdateWireInput,
} from './api';

export function SalesOrderDetail({ salesOrderId }: { salesOrderId: string }) {
  const { data: so, isLoading, isError } = useSalesOrder(salesOrderId);

  if (isLoading) return <Loader />;
  if (isError || !so) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Sales order not found.
      </Alert>
    );
  }

  return so.status === 'DRAFT' ? <DraftSalesOrderEditor key={so.id} so={so} /> : <SalesOrderView so={so} />;
}

function toLineFormRows(so: SalesOrderResponse): SalesOrderLineFormRow[] {
  return so.lines.map((line) => ({
    key: line.id,
    productId: line.productId,
    productLabel: '',
    qty: line.qty,
    priceRand: centsToRand(line.priceCents),
  }));
}

function DraftSalesOrderEditor({ so }: { so: SalesOrderResponse }) {
  const canSell = useCanSell();
  const { data: products } = useAllProducts();
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);

  const updateSalesOrder = useUpdateSalesOrder(so.id);
  const openSalesOrder = useOpenSalesOrder(so.id);
  const cancelSalesOrder = useCancelSalesOrder(so.id);

  const [siteId, setSiteId] = useState(so.siteId);
  const [customerId, setCustomerId] = useState(so.customerId);
  const [reference, setReference] = useState(so.reference ?? '');
  const [notes, setNotes] = useState(so.notes ?? '');
  const [lines, setLines] = useState<SalesOrderLineFormRow[]>(() => (so.lines.length ? toLineFormRows(so) : [emptySalesOrderLineRow()]));
  const [error, setError] = useState<string | null>(null);

  const linesValid = salesOrderLinesValid(lines, productsById);

  function buildPayload(): SalesOrderUpdateWireInput {
    return {
      siteId,
      customerId,
      reference: reference.trim() || null,
      notes: notes.trim() || null,
      lines: toSalesOrderLineInputs(lines),
    };
  }

  async function handleSave() {
    setError(null);
    try {
      await updateSalesOrder.mutateAsync(buildPayload());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    }
  }

  async function handleOpen() {
    setError(null);
    try {
      await updateSalesOrder.mutateAsync(buildPayload());
      await openSalesOrder.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to open the sales order.');
    }
  }

  async function handleCancel() {
    setError(null);
    try {
      await cancelSalesOrder.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to cancel the sales order.');
    }
  }

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{so.number}</Title>
          <DocStatusBadge status={so.status} domain="salesOrder" />
        </Group>
        <Button component={Link} to="/sales-invoicing/sales-orders" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <Group grow>
            <SiteSelect label="Site" value={siteId} onChange={setSiteId} required disabled={!canSell} />
            <CustomerSelect label="Customer" value={customerId} onChange={(id) => setCustomerId(id)} required disabled={!canSell} />
          </Group>
          <Group grow>
            <Textarea label="Reference (optional)" autosize minRows={1} value={reference} onChange={(e) => setReference(e.currentTarget.value)} disabled={!canSell} />
            <Textarea label="Notes (optional)" autosize minRows={1} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} disabled={!canSell} />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Lines
          </Text>
          <SalesOrderLinesEditor rows={lines} onChange={setLines} productsById={productsById} />
        </Stack>
      </Paper>

      {canSell && (
        <Stack gap="xs">
          <Group justify="space-between">
            <ConfirmButton
              color="red"
              variant="light"
              loading={cancelSalesOrder.isPending}
              label="Cancel"
              message={`Cancel sales order ${so.number}? It will be marked CANCELLED and can no longer be edited, opened, or picked in Dispatch a load — this can't be undone from here.`}
              onConfirm={handleCancel}
            />
            <Group>
              <Button variant="default" onClick={() => void handleSave()} loading={updateSalesOrder.isPending && !openSalesOrder.isPending}>
                Save changes
              </Button>
              <Button onClick={() => void handleOpen()} loading={openSalesOrder.isPending} disabled={!linesValid}>
                Open
              </Button>
            </Group>
          </Group>
          <Text size="sm" c="dimmed">
            Opening moves this order from DRAFT to OPEN, makes it selectable from the "Sales order" picker in Dispatch a
            load, and locks it from further editing here.
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

function SalesOrderView({ so }: { so: SalesOrderResponse }) {
  const canSell = useCanSell();
  const { data: products } = useAllProducts();
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);
  const productNameFor = (id: string) => {
    const p = productsById.get(id);
    return p ? `${p.sku} — ${p.name}` : id;
  };

  const completeSalesOrder = useCompleteSalesOrder(so.id);
  const cancelSalesOrder = useCancelSalesOrder(so.id);
  const [error, setError] = useState<string | null>(null);

  const dispatchesQuery = useDispatchesForCustomer(so.customerId);
  const linkedDispatches = (dispatchesQuery.data?.items ?? []).filter((d) => d.salesOrderId === so.id);

  async function handleCancel() {
    setError(null);
    try {
      await cancelSalesOrder.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to cancel the sales order.');
    }
  }

  async function handleComplete() {
    setError(null);
    try {
      await completeSalesOrder.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to complete the sales order.');
    }
  }

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{so.number}</Title>
          <DocStatusBadge status={so.status} domain="salesOrder" />
        </Group>
        <Button component={Link} to="/sales-invoicing/sales-orders" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Site:</Text>
            <SiteName id={so.siteId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Customer:</Text>
            <CustomerName id={so.customerId} />
          </Group>
          {so.reference && (
            <Group gap={6}>
              <Text fw={600}>Reference:</Text>
              <Text>{so.reference}</Text>
            </Group>
          )}
          {so.notes && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Notes:</Text>
              <Text>{so.notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th ta="right">Price</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {so.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>{productNameFor(line.productId)}</Table.Td>
                  <Table.Td>{formatLineQty(line.qty, productsById.get(line.productId)?.baseUom)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.priceCents)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {so.status === 'OPEN' && (
        <Alert color="blue" variant="light">
          Dispatches do not complete this order automatically — mark it complete when fulfilled.
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Dispatches against this order
          </Text>
          {dispatchesQuery.isLoading && <Loader size="sm" />}
          {/* Truncation disclosure (wave-gate finding, 2026-07-10): this panel
              checks ONE page of the customer's dispatches (LOOKUP_LIMIT = the
              server max; see useDispatchesForCustomer's caveat). The list
              endpoint orders by id ascending — chronological — so when more
              exist (nextCursor non-null) it is the NEWEST dispatches that are
              missing here, and the operator deciding whether to press
              Complete must know this list may be incomplete. */}
          {dispatchesQuery.data?.nextCursor != null && (
            <Alert color="yellow" variant="light" p="xs">
              <Text size="sm">
                This customer has more than {LOOKUP_LIMIT} dispatches — only the oldest {LOOKUP_LIMIT} were checked, so
                recent dispatches against this order may be missing below. See the{' '}
                <Link to="/sales-invoicing/dispatches">Dispatches register</Link> for the full history before marking
                this order complete.
              </Text>
            </Alert>
          )}
          {!dispatchesQuery.isLoading && linkedDispatches.length === 0 && (
            <Text c="dimmed" size="sm">
              No dispatches have been posted against this order yet.
            </Text>
          )}
          {linkedDispatches.length > 0 && (
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Dispatched</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {linkedDispatches.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td>
                        <Link to="/sales-invoicing/dispatches/$dispatchId" params={{ dispatchId: d.id }}>
                          {d.number}
                        </Link>
                      </Table.Td>
                      <Table.Td>{formatDateTime(d.dispatchedAt)}</Table.Td>
                      <Table.Td>
                        <DocStatusBadge status={d.status} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>

      {/* SalesOrderDetail only ever renders this component for a non-DRAFT order
          (DRAFT routes to DraftSalesOrderEditor above) — Cancel is reachable
          from OPEN here (DRAFT's own Cancel lives on that other component). */}
      {canSell && so.status === 'OPEN' && (
        <Group justify="space-between">
          <ConfirmButton
            color="red"
            variant="light"
            loading={cancelSalesOrder.isPending}
            label="Cancel"
            message={`Cancel sales order ${so.number}? It will be marked CANCELLED and can no longer be edited, opened, or picked in Dispatch a load. Any dispatches already posted against it are not affected — this can't be undone from here.`}
            onConfirm={handleCancel}
          />
          <ConfirmButton
            loading={completeSalesOrder.isPending}
            label="Complete"
            message={`Mark sales order ${so.number} as COMPLETE? This is a manual step — the system does not check whether every expected load has been dispatched, and posting a dispatch never completes an order automatically. Once COMPLETE, it can no longer be edited, reopened, or picked in Dispatch a load — this can't be undone from here.`}
            onConfirm={handleComplete}
          />
        </Group>
      )}

      {so.status === 'COMPLETE' && (
        <Text c="dimmed" size="sm">
          This sales order is complete. It can no longer be edited, reopened, or picked in Dispatch a load.
        </Text>
      )}
      {so.status === 'CANCELLED' && (
        <Text c="dimmed" size="sm">
          This sales order has been cancelled. Any dispatches already posted against it were not affected.
        </Text>
      )}
    </Stack>
  );
}
